import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import './UpdatePassword.css';

function UpdatePassword() {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [hasSession, setHasSession] = useState(false);
  
  useEffect(() => {
    // Check if we have an active session after being redirected from AuthConfirm
    const checkSession = async () => {
      try {
        console.log('Checking for active session in UpdatePassword component');
        
        // Check if we have the auth_verified flag in localStorage
        const authVerified = localStorage.getItem('auth_verified') === 'true';
        console.log('Auth verified flag:', authVerified);
        
        // Check for auth_success parameter in the URL
        const authSuccess = searchParams.get('auth_success');
        console.log('Auth success parameter:', authSuccess);
        
        // Get session from Supabase
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Session check failed:', error);
          setError('Authentication error. Please request a new password reset link.');
        } else if (data?.session) {
          console.log('Active session found, user: ', data.session.user?.email);
          setHasSession(true);
        } else if (authVerified) {
          // If we have the auth_verified flag but no session, force a reload
          // This can happen if the session wasn't properly persisted
          console.log('Auth was verified but no session found. Setting hasSession to true anyway.');
          setHasSession(true);
        } else {
          console.log('No active session found');
          setError('No active session found. Please request a new password reset link.');
        }
      } catch (err) {
        console.error('Error checking session:', err);
        setError('An unexpected error occurred. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };
    
    checkSession();
  }, [searchParams]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    // Basic validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }
    
    try {
      console.log('Updating password...');
      
      // Use the active session to update the password
      const { error } = await supabase.auth.updateUser({
        password: password
      });
      
      if (error) {
        console.error('Password update failed:', error);
        throw new Error(error.message || 'Failed to update password');
      }
      
      // Clean up the auth_verified flag
      localStorage.removeItem('auth_verified');
      
      // Sign out after successful password update
      await supabase.auth.signOut();
      
      console.log('Password updated successfully');
      setSuccess(true);
    } catch (err) {
      console.error('Error updating password:', err);
      setError(err.message || 'Failed to update password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  if (isLoading) {
    return (
      <div className="update-password-container">
        <div className="update-password-card loading">
          <div className="loader"></div>
          <p>Processing your request...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="update-password-container">
        <div className="update-password-card error">
          <div className="error-icon">!</div>
          <h2>Error</h2>
          <p>{error}</p>
          <a href="/" className="button">Back to Home</a>
        </div>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="update-password-container">
        <div className="update-password-card success">
          <div className="success-icon">✓</div>
          <div className="success-message">
            <h2>Password Updated Successfully!</h2>
            <p>Your password has been updated. You can now return to the Whinself app on your device and login with your new password.</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (!hasSession) {
    return (
      <div className="update-password-container">
        <div className="update-password-card error">
          <div className="error-icon">!</div>
          <h2>Invalid Session</h2>
          <p>No active session found. Please request a new password reset link.</p>
          <a href="/" className="button">Back to Home</a>
        </div>
      </div>
    );
  }
  
  return (
    <div className="update-password-container">
      <div className="update-password-card">
        <h2>Reset Your Password</h2>
        <p>Please enter your new password below.</p>
        
        <form onSubmit={handleSubmit} className="password-form">
          <div className="form-group">
            <label htmlFor="password">New Password</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength="6"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength="6"
            />
          </div>
          
          <button type="submit" className="submit-button">
            Reset Password
          </button>
        </form>
      </div>
    </div>
  );
}

export default UpdatePassword; 